import { useCallback, useEffect, useRef } from 'react';

const FOCUS_STABILIZATION_FRAME_COUNT = 12;

interface Params {
    isAliasChangeCustomizationActive: boolean;
    overviewCardDataCy: string;
}

interface Result {
    handleAliasChangeSaved: () => void;
}

export const useAliasSaveFocusRestoration = ({
    isAliasChangeCustomizationActive,
    overviewCardDataCy,
}: Params): Result => {
    const shouldRestoreMenuFocusRef = useRef(false);
    const focusRestoreAnimationFrameRef = useRef<number | null>(null);
    const focusStabilizationAnimationFrameRef = useRef<number | null>(null);
    const focusStabilizationFramesRemainingRef = useRef(0);

    const handleAliasChangeSaved = (): void => {
        shouldRestoreMenuFocusRef.current = true;
    };

    const focusCardMenuToggle = useCallback(() => {
        const menuToggle = Array.from(
            document.querySelectorAll<HTMLElement>(
                '[data-cy="overview-card-secondary-menu-button"]',
            ),
        ).find(
            (toggle) =>
                toggle.parentElement
                    ?.closest('[data-cy^="overview-card-"]')
                    ?.getAttribute('data-cy') === overviewCardDataCy,
        );

        if (menuToggle && document.activeElement !== menuToggle) {
            menuToggle.focus({ preventScroll: true });
        }
    }, [overviewCardDataCy]);

    useEffect(() => {
        const clearFocusRestoration = (): void => {
            if (focusRestoreAnimationFrameRef.current !== null) {
                cancelAnimationFrame(focusRestoreAnimationFrameRef.current);
                focusRestoreAnimationFrameRef.current = null;
            }
            if (focusStabilizationAnimationFrameRef.current !== null) {
                cancelAnimationFrame(focusStabilizationAnimationFrameRef.current);
                focusStabilizationAnimationFrameRef.current = null;
            }
            focusStabilizationFramesRemainingRef.current = 0;
        };

        if (!isAliasChangeCustomizationActive && shouldRestoreMenuFocusRef.current) {
            focusRestoreAnimationFrameRef.current = requestAnimationFrame(() => {
                focusRestoreAnimationFrameRef.current = null;
                focusCardMenuToggle();
                shouldRestoreMenuFocusRef.current = false;
                focusStabilizationFramesRemainingRef.current = FOCUS_STABILIZATION_FRAME_COUNT;

                const stabilizeFocus = (): void => {
                    if (focusStabilizationFramesRemainingRef.current <= 0) {
                        focusStabilizationAnimationFrameRef.current = null;
                        return;
                    }

                    focusCardMenuToggle();
                    focusStabilizationFramesRemainingRef.current -= 1;
                    focusStabilizationAnimationFrameRef.current =
                        requestAnimationFrame(stabilizeFocus);
                };

                focusStabilizationAnimationFrameRef.current = requestAnimationFrame(stabilizeFocus);
            });
        }

        return clearFocusRestoration;
    }, [focusCardMenuToggle, isAliasChangeCustomizationActive]);

    return { handleAliasChangeSaved };
};
